import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

/**
 * Renders an API timestamp in Kuwait time (Asia/Kuwait, a fixed +03:00 with no DST).
 *
 * Every timestamp the API stores is UTC - the BLL writes DateTime.UtcNow everywhere - so a
 * value is only ever right on screen once it has been shifted to Kuwait. toLocalDate shifts it
 * to whatever timezone the viewer's machine happens to be set to instead, which quietly shows a
 * different time to every user; this pins the output to Kuwait for all of them.
 *
 * The API is also inconsistent about marking a value as UTC, so the string has to be pinned down
 * before it's parsed:
 *  - nullable DateTime? properties go through JsonDateTimeConverter and arrive with no timezone
 *    designator at all ("2026-09-22T09:10:19.123456"),
 *  - a non-nullable DateTime that hasn't been round-tripped through SQL Server keeps Kind=Utc and
 *    arrives with a trailing "Z" (e.g. the estimation returned straight out of SendAll).
 * JavaScript parses the first form as *local* time, so without tagging it the same instant lands
 * hours apart depending on which endpoint returned it.
 */
@Pipe({ name: 'toKuwaitDate' })
export class ToKuwaitDatePipe implements PipeTransform {

  private static readonly KUWAIT_OFFSET = '+0300';

  constructor(private datePipe: DatePipe) { }

  transform(value: any, format?: string, locale?: string): string | null {
    if (value === null || value === undefined || value === '') { return null; }
    return this.datePipe.transform(this.asUtcInstant(value), format, ToKuwaitDatePipe.KUWAIT_OFFSET, locale);
  }

  private asUtcInstant(value: any): any {
    if (typeof value !== 'string') { return value; }

    const trimmed = value.trim();

    // A date-only value ("2026-09-22") is already parsed as UTC midnight by JavaScript, and a
    // value that carries its own designator ("...Z", "...+03:00") is unambiguous - leave both.
    if (!/^\d{4}-\d{2}-\d{2}T/.test(trimmed) || /(Z|[+\-]\d{2}:?\d{2})$/.test(trimmed)) {
      return trimmed;
    }

    // .NET writes 6-7 fractional-second digits; only 3 parse reliably in the browser.
    return trimmed.replace(/(\.\d{1,3})\d*/, '$1') + 'Z';
  }
}
